import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';

import { SortingOrders } from 'config/constants';
import { AuthActions } from 'store/Auth';
import { BundleMigrationActions } from 'store/BundleMigration';
import { DeviceThunks } from 'store/Device';
import { UserActions } from 'store/User';

import SettingActions from './actions';
import { resetInterfaceSettings } from './utils';
import type {
  Category,
  OrderKind,
  SelectedSoundAndAlertConfig,
  SettingState,
} from 'store/Settings/types';

export const initialSettingsState: SettingState = {
  allCategories: [],
  selectedCategories: [],
  isSelectedAllCategories: true,
  selectedOrderKinds: [],
  isSelectedAllOrderKinds: true,
  isItemCookDelayEnabled: false,
  itemCookDelayInSeconds: 2,
  ordersSortingOrder: SortingOrders.BY_START_PREPARATION_STATUS_DESC,
  loopNotificationSound: false,
  isCountDownTimerEnabled: true,
  isCountDownTimerBlinkingThresholdEnabled: true,
  countDownTimerBlinkingThreshold: 20,
  isGridViewCollapsed: false,
  interface: {
    itemList: {
      shouldDisplayActionButton: true,
      shouldDisplayInternalNotes: true,
      shouldDisplayItemPricing: true,
    },
    itemDetails: {
      shouldDisplayActionButton: true,
      shouldDisplayInternalNotes: true,
      shouldDisplayDriverDetails: true,
      shouldDisplayOrderChannel: true,
      shouldDisplayCustomerName: true,
      shouldDisplayCustomerAddress: true,
    },
    orderList: {
      shouldDisplayCustomerName: true,
      shouldDisplayCustomerAddress: true,
      shouldDisplayCustomerPhoneNumber: true,
    },
    orderDetails: {
      shouldDisplayCustomFields: true,
      shouldDisplayActionButtonHeader: true,
      shouldDisplayActionButtonItems: true,
      shouldDisplayFinancialInfo: true,
      shouldDisplayCustomerAddress: true,
      shouldDisplayDriverDetails: true,
      shouldDisplayItemPricing: true,
      shouldDisplayOrderChannel: true,
      shouldDisplayInternalNotes: true,
    },
    orderHeader: {
      shouldDisplayCustomFields: true,
      shouldDisplayCustomerAddress: true,
      shouldDisplayCustomerName: true,
      shouldDisplayCustomerPhoneNumber: true,
      shouldDisplayDriverDetails: true,
      shouldDisplayNumberOfItems: true,
      shouldDisplayOrderChannel: true,
      shouldDisplayTotalOrderValue: true,
      shouldDisplayOrderType: true,
    },
    listViewPreview: {
      shouldDisplayCustomerAddress: true,
      shouldDisplayCustomerName: true,
      shouldDisplayCustomerPhoneNumber: true,
      shouldDisplayNumberOfItems: true,
      shouldDisplayTotalOrderValue: true,
    },
    actionButtons: {
      shouldDisplayAllActionButton: true,
      shouldDisplayIndividualActionButton: true,
    },
  },
  cardViewLayout: {
    layout: 'default',
    statusTab: 'classic',
    allTabColumn: undefined,
    cookingTabColumn: undefined,
    newTabColumn: undefined,
    collapsibility: true,
  },
  selectedSoundAndAlertConfig: undefined,
};

const slice = createSlice({
  name: 'settings',
  initialState: initialSettingsState,
  reducers: {
    setSelectedCategories: (state, action: PayloadAction<Category[]>) => {
      state.selectedCategories = action.payload;
    },
    toggleAllCategories: (state, action: PayloadAction<boolean>) => {
      state.isSelectedAllCategories = action.payload;
    },
    setSelectedOrderKinds: (state, action: PayloadAction<OrderKind[]>) => {
      state.selectedOrderKinds = action.payload;
    },
    toggleAllOrderKinds: (state, action: PayloadAction<boolean>) => {
      state.isSelectedAllOrderKinds = action.payload;
    },
    setOrdersSorting: (state, action) => {
      state.ordersSortingOrder = action.payload;
    },
    toggleItemCookDelayEnabled: (state, action) => {
      state.isItemCookDelayEnabled = action.payload;
    },
    setItemCookDelay: (state, action: PayloadAction<number>) => {
      state.itemCookDelayInSeconds = action.payload;
    },
    setRepeatSoundsOptions: (state, action) => {
      state.loopNotificationSound = action.payload;
    },
    toggleCountDownTimer: (state, action) => {
      state.isCountDownTimerEnabled = action.payload;
    },
    toggleCountDownBlinkingThresholdEnabled: (state, action) => {
      state.isCountDownTimerBlinkingThresholdEnabled = action.payload;
    },
    setCountDownTimerBlinkingThreshold: (state, action) => {
      state.countDownTimerBlinkingThreshold = action.payload;
    },
    setItemListInterface: (state, action) => {
      const newState = {
        ...state.interface.itemList,
        ...action.payload,
      };

      state.interface = {
        ...state.interface,
        itemList: newState,
      };
    },
    setOrderHeaderInterface: (state, action) => {
      const newState = {
        ...state.interface.orderHeader,
        ...action.payload,
      };

      state.interface = {
        ...state.interface,
        orderHeader: newState,
      };
    },
    setListViewPreviewInterface: (state, action) => {
      const newState = {
        ...state.interface.listViewPreview,
        ...action.payload,
      };

      state.interface = {
        ...state.interface,
        listViewPreview: newState,
      };
    },
    setActionButtonsInterface: (state, action) => {
      const newState = {
        ...state.interface.actionButtons,
        ...action.payload,
      };

      state.interface = {
        ...state.interface,
        actionButtons: newState,
      };
    },
    setItemDetailsInterface: (state, action) => {
      const newState = {
        ...state.interface.itemDetails,
        ...action.payload,
      };

      state.interface = {
        ...state.interface,
        itemDetails: newState,
      };
    },
    setOrderListInterface: (state, action) => {
      const newState = {
        ...state.interface.orderList,
        ...action.payload,
      };

      state.interface = {
        ...state.interface,
        orderList: newState,
      };
    },
    setOrderDetailsInterface: (state, action) => {
      const newState = {
        ...state.interface.orderDetails,
        ...action.payload,
      };

      state.interface = {
        ...state.interface,
        orderDetails: newState,
      };
    },
    setSoundAndAlertConfig: (
      state,
      action: PayloadAction<SelectedSoundAndAlertConfig>,
    ) => {
      state.selectedSoundAndAlertConfig = action.payload;
    },

    setCardViewLayoutLayout: (
      state,
      action: PayloadAction<SettingState['cardViewLayout']['layout']>,
    ) => {
      state.cardViewLayout.layout = action.payload;
    },
    setCardViewLayoutStatusTab: (
      state,
      action: PayloadAction<SettingState['cardViewLayout']['statusTab']>,
    ) => {
      state.cardViewLayout.statusTab = action.payload;
    },
    setCardViewLayoutAllTabColumn: (
      state,
      action: PayloadAction<SettingState['cardViewLayout']['allTabColumn']>,
    ) => {
      state.cardViewLayout.allTabColumn = action.payload;
    },
    setCardViewLayoutNewTabColumn: (
      state,
      action: PayloadAction<SettingState['cardViewLayout']['newTabColumn']>,
    ) => {
      state.cardViewLayout.newTabColumn = action.payload;
    },
    setCardViewLayoutCookingTabColumn: (
      state,
      action: PayloadAction<SettingState['cardViewLayout']['cookingTabColumn']>,
    ) => {
      state.cardViewLayout.cookingTabColumn = action.payload;
    },
    setCardViewLayoutCollapsibility: (
      state,
      action: PayloadAction<SettingState['cardViewLayout']['collapsibility']>,
    ) => {
      state.cardViewLayout.collapsibility = action.payload;
    },

    setIsGridViewCollapsed: (
      state,
      action: PayloadAction<SettingState['isGridViewCollapsed']>,
    ) => {
      state.isGridViewCollapsed = action.payload;
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(SettingActions.fetchSuccess, (state, action) => {
        const { items } = action.payload;
        state.allCategories = items;
        // these categories were selected, but they aren't presented in 'all' anymore
        const missingCategories = _.difference(
          state.selectedCategories,
          state.allCategories,
        );
        if (missingCategories.length) {
          state.selectedCategories = _.without(
            state.selectedCategories,
            ...missingCategories,
          );
        }
      })
      .addCase(UserActions.fetchAccountSuccess, (state, action) => {
        const { interface_settings: interfaceSettings } = action.payload;
        resetInterfaceSettings(state, interfaceSettings);
      })
      .addCase(UserActions.selectAccount, (state, action) => {
        const account = action.payload;

        if (account) {
          const interfaceSettings = account.interface_settings;
          resetInterfaceSettings(state, interfaceSettings);
        }
      })
      .addCase(DeviceThunks.setScreen.fulfilled, (state) => {
        state.isSelectedAllCategories = false;
        state.selectedCategories = [];

        state.isSelectedAllOrderKinds = false;
        state.selectedOrderKinds = [];
      })
      .addCase(AuthActions.signOut, (state) => {
        state.isSelectedAllCategories =
          initialSettingsState.isSelectedAllCategories;
        state.selectedCategories = initialSettingsState.selectedCategories;

        state.isSelectedAllOrderKinds =
          initialSettingsState.isSelectedAllOrderKinds;
        state.selectedOrderKinds = initialSettingsState.selectedOrderKinds;
      })
      .addCase(BundleMigrationActions.setCompleted, (state, action) => {
        const { categories } = action.payload.migratedState;

        return { ...initialSettingsState, ...categories };
      }),
});

export const sliceActions = slice.actions;
export const reducer = slice.reducer;
